import React, { useState } from 'react';

import PermissionTooltip from '@dashboard/components/PermissionTooltip';
import {
  type FPlatformFragment,
  PlatformHaltedStatus,
  useUpdatePlatformHaltedStatusMutation,
} from '@dashboard/generated/wp/authentication';
import useCurrentWpPlatform from '@dashboard/hooks/useCurrentWpPlatform';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

import { alertError, alertSuccess } from '@waypoint/react/notifications';

import { usePermission } from '../hooks';

import DangerZoneRow from './DangerZoneRow';

type PlatformHaltAction = 'disable' | 'enable';

type TogglePlatformHaltRowProps = {
  platform: FPlatformFragment;
};

export default function TogglePlatformHaltRow({ platform }: TogglePlatformHaltRowProps) {
  const permission = usePermission('haltPlatformEmailMessageSending');
  const [dialogAction, setDialogAction] = useState<PlatformHaltAction | null>(null);

  let description: string;
  let buttonLabel: string;
  let buttonVariant: 'outlined' | 'contained';
  let buttonColor: 'primary' | 'error';

  if (platform.isHalted) {
    description = 'Email sending is disabled for this workspace.';
    buttonLabel = 'Re-enable sending';
    buttonVariant = 'contained';
    buttonColor = 'primary';
  } else {
    description = 'Turn off email sending for this workspace.';
    buttonLabel = 'Disable sending';
    buttonVariant = 'outlined';
    buttonColor = 'error';
  }

  const renderDialog = () => {
    if (!dialogAction) {
      return null;
    }
    return <TogglePlatformHaltDialog action={dialogAction} platform={platform} onClose={() => setDialogAction(null)} />;
  };

  return (
    <>
      <DangerZoneRow
        label="Email sending"
        description={description}
        action={
          <PermissionTooltip span name="haltPlatformEmailMessageSending">
            <Button
              disabled={!permission}
              variant={buttonVariant}
              color={buttonColor}
              onClick={() => setDialogAction(platform.isHalted ? 'enable' : 'disable')}
            >
              {buttonLabel}
            </Button>
          </PermissionTooltip>
        }
      />
      {renderDialog()}
    </>
  );
}

type TogglePlatformHaltDialogProps = {
  action: PlatformHaltAction;
  platform: FPlatformFragment;
  onClose: () => void;
};

function TogglePlatformHaltDialog({ action, platform, onClose }: TogglePlatformHaltDialogProps) {
  const { refetch: refetchPlatform } = useCurrentWpPlatform();
  const [updatePlatformHaltedStatus, { loading }] = useUpdatePlatformHaltedStatusMutation();

  const isEnabling = action === 'enable';

  const title = isEnabling ? 'Re-enable email sending?' : 'Disable email sending?';
  const description = isEnabling
    ? `New live emails will send again. Emails sent while sending was disabled won't be sent.`
    : `New live emails will be logged in Activity but won't be sent.`;
  const buttonLabel = isEnabling ? 'Yes, re-enable sending' : 'Yes, disable sending';
  const buttonColor = isEnabling ? 'primary' : 'error';

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const handleSubmit = async () => {
    const platformId = platform.id;
    try {
      const status = isEnabling ? PlatformHaltedStatus.Active : PlatformHaltedStatus.Halted;
      await updatePlatformHaltedStatus({ variables: { platformId, status } });
      onClose();
      alertSuccess(isEnabling ? 'Email sending re-enabled' : 'Email sending disabled');
      refetchPlatform().catch(() => undefined);
    } catch {
      alertError('Failed to update email sending');
    }
  };

  return (
    <Dialog maxWidth="xs" open onClose={handleClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary">{description}</Typography>
      </DialogContent>
      <DialogActions>
        <Button disabled={loading} onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={loading} variant="contained" color={buttonColor} onClick={handleSubmit}>
          {buttonLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
